import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { AppraisalResponse } from "types/appraisal-response-types";

interface Employee {
  full_name: string;
  designation?: string;
  department?: string;
}

export const generateAppraisalPDF = (
  cycleName: string,
  employee: Employee,
  responses: AppraisalResponse[]
) => {
  const doc = new jsPDF();

  const brandColor: [number, number, number] = [6, 57, 112];
  const darkText: [number, number, number] = [44, 62, 80];

  /* ------------------------------------------------ */
  /* 1. SAFE CYCLE WEIGHTS EXTRACTION                */
  /* ------------------------------------------------ */
  const cycleRef = (responses?.[0] as any)?.cycle || {};

  const DYNAMIC_WEIGHTS: Record<string, number> = {
    MANAGER: Number(cycleRef.manager_review_weightage) || 35,
    CLIENT: Number(cycleRef.client_review_weightage) || 25,
    PEER: Number(cycleRef.peer_review_weightage) || 25,
    SELF: Number(cycleRef.self_review_weightage) || 15,
  };

  console.log("Cycle Weights:", DYNAMIC_WEIGHTS);

  /* ------------------------------------------------ */
  /* 2. CALCULATION ENGINE                           */
  /* ------------------------------------------------ */
  let weightedContributionSum = 0;
  let activeCycleWeightageTotal = 0;

  const scoreSummary = responses.map((res) => {
    let reviewerEarned = 0;
    let reviewerPossible = 0;

    res.question_responses?.forEach((qr) => {
      const qWeight = Number(qr.question_weightage) || 0;
      const qRating = Number(qr.rating) || 0;

      reviewerEarned += (qRating / 5) * qWeight;
      reviewerPossible += qWeight;
    });

    const performanceRatio =
      reviewerPossible > 0 ? reviewerEarned / reviewerPossible : 0;

    const scoreOutof100 = performanceRatio * 100;

    const typeKey = String(res.reviewer_type || "").toUpperCase().trim();
    const weightInCycle = Number(DYNAMIC_WEIGHTS[typeKey]) || 0;

    if (weightInCycle > 0 && reviewerPossible > 0) {
      weightedContributionSum += scoreOutof100 * (weightInCycle / 100);
      activeCycleWeightageTotal += weightInCycle;
    }

    /* DEBUG LOG */
    console.log("Reviewer Debug →", {
      type: typeKey,
      earned: reviewerEarned,
      possible: reviewerPossible,
      score: scoreOutof100,
      weight: weightInCycle,
      contribution: scoreOutof100 * (weightInCycle / 100),
    });

    return {
      type: typeKey,
      score: scoreOutof100,
      weight: weightInCycle,
      contribution: scoreOutof100 * (weightInCycle / 100),
    };
  });

  const totalPerformanceIndex =
    activeCycleWeightageTotal > 0 && !isNaN(weightedContributionSum)
      ? weightedContributionSum / (activeCycleWeightageTotal / 100)
      : 0;

  console.log("FINAL INDEX:", totalPerformanceIndex);

  /* ------------------------------------------------ */
  /* 3. HEADER                                        */
  /* ------------------------------------------------ */
  doc.setFillColor(...brandColor);
  doc.rect(0, 0, 210, 45, "F");

  doc.setFontSize(22);
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.text("Performance Evaluation Report", 14, 18);

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Cycle: ${cycleName}`, 14, 26);

  doc.setFillColor(255, 255, 255, 0.2);
  doc.roundedRect(14, 31, 85, 10, 1, 1, "F");

  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text(
    `TOTAL PERFORMANCE INDEX: ${totalPerformanceIndex.toFixed(2)}%`,
    18,
    37.5
  );

  doc.setFontSize(14);
  doc.text(employee.full_name.toUpperCase(), 196, 18, { align: "right" });

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(employee.designation || "Executive", 196, 26, { align: "right" });

  /* ------------------------------------------------ */
  /* 4. SUMMARY TABLE                                 */
  /* ------------------------------------------------ */
  let currentY = 55;

  doc.setTextColor(...darkText);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Executive Summary", 14, currentY);

  autoTable(doc, {
    startY: currentY + 4,
    head: [["Reviewer Category", "Weight", "Score", "Contribution"]],
    body: scoreSummary
      .sort((a, b) => b.weight - a.weight)
      .map((s) => [
        s.type,
        `${s.weight.toFixed(0)}%`,
        `${s.score.toFixed(2)}%`,
        `${s.contribution.toFixed(2)}%`,
      ]),
    theme: "striped",
    headStyles: { fillColor: brandColor },
    styles: { fontSize: 10, font: "helvetica" },
  });

  currentY = (doc as any).lastAutoTable.finalY + 15;

  /* ------------------------------------------------ */
  /* 5. DETAILED SECTIONS                             */
  /* ------------------------------------------------ */
  const priorityMap = { MANAGER: 1, CLIENT: 2, PEER: 3, SELF: 4 };

  const sortedResponses = [...responses].sort(
    (a, b) =>
      (priorityMap[String(a.reviewer_type).toUpperCase() as keyof typeof priorityMap] ?? 99) -
      (priorityMap[String(b.reviewer_type).toUpperCase() as keyof typeof priorityMap] ?? 99)
  );

  sortedResponses.forEach((res) => {
    if (currentY > 230) {
      doc.addPage();
      currentY = 20;
    }

    const typeKey = String(res.reviewer_type).toUpperCase();

    doc.setTextColor(...brandColor);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(`${typeKey} ASSESSMENT DETAILS`, 14, currentY);

    autoTable(doc, {
      startY: currentY + 4,
      head: [["KPI", "Rating", "Comments"]],
      body: res.question_responses.map((qr) => [
        (qr as any).question_text || "Metric",
        `${Number(qr.rating) || 0} / 5`,
        qr.comment || "No comments provided",
      ]),
      theme: "grid",
      headStyles: { fillColor: [240, 240, 240], textColor: [50, 50, 50] },
      styles: { fontSize: 9, cellPadding: 4, font: "helvetica" },
      columnStyles: {
        0: { cellWidth: 50, fontStyle: "bold" },
        1: { cellWidth: 20, halign: "center" },
        2: { cellWidth: "auto" },
      },
    });

    currentY = (doc as any).lastAutoTable.finalY + 15;
  });

  /* ------------------------------------------------ */
  /* 6. FOOTER                                        */
  /* ------------------------------------------------ */
  const pageCount = doc.getNumberOfPages();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(170, 170, 170);
    doc.text(
      `Proprietary & Confidential | ${employee.full_name} | Page ${i} of ${pageCount}`,
      105,
      287,
      { align: "center" }
    );
  }

  doc.save(`Performance_Report_${employee.full_name.replace(/\s+/g, "_")}.pdf`);
};
